import { memo } from 'react';
import { Banknote, Building, CircleDollarSign, type LucideIcon } from 'lucide-react';
import { PaymentMethod, PaymentStatus } from '@/apis/payment';
import { ShippingType, StatusOrders } from '@/apis/orders';
import type { Orders } from '@/apis/orders';
import { DynamicIcon } from '@/components/Icon';
import type { IconName } from '@/components/Icon';
import { DynamicImage } from '@/components/Atoms/Image';
import type { ImageName } from '@/components/Atoms/Image';
import aftermarketImg from '@assets/images/aftermarket.png';
import amazonImg from '@assets/images/amazon.png';
import ebayImg from '@assets/images/ebay.png';
import walmartImg from '@assets/images/wallmart.png';
import zohoImg from '@assets/images/zoho.png';
import gtEngine from '@assets/images/gt-engine.png';

export const ORDER_STATUS_STYLE: Record<string, { label: string; bg: string; ringColor: string; iconColor: string }> = {
  [StatusOrders.PENDING]:                     { label: 'Pending',                  bg: 'bg-warning-light text-warning',       ringColor: 'bg-warning-light border-warning',      iconColor: 'text-warning' },
  [StatusOrders.AWAITING_PAYMENT]:            { label: 'Awaiting Payment',         bg: 'bg-warning-light text-warning',       ringColor: 'bg-warning-light border-warning',      iconColor: 'text-warning' },
  [StatusOrders.PROCESSING]:                  { label: 'Processing',               bg: 'bg-purple-100 text-purple-600',       ringColor: 'bg-purple-100 border-purple-600',      iconColor: 'text-purple-600' },
  [StatusOrders.PAID]:                        { label: 'Paid',                     bg: 'bg-success-light text-success',       ringColor: 'bg-success-light border-success',      iconColor: 'text-success' },
  [StatusOrders.SHIPPED]:                     { label: 'Shipped',                  bg: 'bg-success-light text-success',       ringColor: 'bg-success-light border-success',      iconColor: 'text-success' },
  [StatusOrders.COMPLETED]:                   { label: 'Completed',                bg: 'bg-success-light text-success',       ringColor: 'bg-success-light border-success',      iconColor: 'text-success' },
  [StatusOrders.CANCELLED]:                   { label: 'Cancelled',                bg: 'bg-smoke text-lightGray',             ringColor: 'bg-smoke border-dark5',               iconColor: 'text-lightGray' },
  [StatusOrders.CANCELLATION_REQUESTED]:      { label: 'Cancellation Requested',   bg: 'bg-danger-light text-danger',         ringColor: 'bg-danger-light border-danger',        iconColor: 'text-danger' },
  [StatusOrders.RETURN_REQUEST]:              { label: 'Return Request',           bg: 'bg-danger-light text-danger',         ringColor: 'bg-danger-light border-danger',        iconColor: 'text-danger' },
  [StatusOrders.REFUNDED]:                    { label: 'Refunded',                 bg: 'bg-danger-light text-danger',         ringColor: 'bg-danger-light border-danger',        iconColor: 'text-danger' },
  [StatusOrders.PARTIAL_REFUND]:              { label: 'Partial Refund',           bg: 'bg-warning-light text-warning',       ringColor: 'bg-warning-light border-warning',      iconColor: 'text-warning' },
  [StatusOrders.ACTION_REQUIRED_BY_CUSTOMER]: { label: 'Action Required',          bg: 'bg-danger-light text-danger',         ringColor: 'bg-danger-light border-danger',        iconColor: 'text-danger' },
  [StatusOrders.REQUEST_DOCUMENT]:            { label: 'Request Document',         bg: 'bg-danger-light text-danger',         ringColor: 'bg-danger-light border-danger',        iconColor: 'text-danger' },
  [StatusOrders.CONTACT_BUYER]:               { label: 'Contact Buyer',            bg: 'bg-warning-light text-warning',       ringColor: 'bg-warning-light border-warning',      iconColor: 'text-warning' },
  [StatusOrders.DOUBLED_ORDER]:               { label: 'Doubled Order',            bg: 'bg-orange-100 text-orange-500',       ringColor: 'bg-orange-100 border-orange-400',      iconColor: 'text-orange-500' },
  [StatusOrders.STRIPE_PENDING]:              { label: 'Stripe Pending',           bg: 'bg-info-light text-info',             ringColor: 'bg-info-light border-info',            iconColor: 'text-info' },
  [StatusOrders.PAYPAL_PENDING]:              { label: 'Paypal Pending',           bg: 'bg-info-light text-info',             ringColor: 'bg-info-light border-info',            iconColor: 'text-info' },
  [StatusOrders.NPA_PENDING]:                 { label: 'NPA Pending',              bg: 'bg-info-light text-info',             ringColor: 'bg-info-light border-info',            iconColor: 'text-info' },
  [StatusOrders.STRIPE_COMPLETED]:            { label: 'Stripe Completed',         bg: 'bg-primary-s50 text-primary-s500',   ringColor: 'bg-primary-s50 border-primary-s500',   iconColor: 'text-primary-s500' },
  [StatusOrders.PAYPAL_COMPLETED]:            { label: 'Paypal Completed',         bg: 'bg-primary-s50 text-primary-s500',   ringColor: 'bg-primary-s50 border-primary-s500',   iconColor: 'text-primary-s500' },
  [StatusOrders.NPA_COMPLETED]:               { label: 'NPA Completed',            bg: 'bg-primary-s50 text-primary-s500',   ringColor: 'bg-primary-s50 border-primary-s500',   iconColor: 'text-primary-s500' },
  [StatusOrders.PAYMENT_ACTION_REQUEST]:      { label: 'Payment Action Request',   bg: 'bg-warning-light text-warning',       ringColor: 'bg-warning-light border-warning',      iconColor: 'text-warning' },
};

export const DEFAULT_ORDER_STATUS_STYLE = {
  bg: 'bg-smoke text-lightGray',
  ringColor: 'bg-smoke1 border-dark5',
  iconColor: 'text-lightGray',
};

export const STATUS_LABELS: Record<string, string> = {
  [StatusOrders.PENDING]:                     'Pending',
  [StatusOrders.AWAITING_PAYMENT]:            'Awaiting Payment',
  [StatusOrders.PROCESSING]:                  'Processing',
  [StatusOrders.COMPLETED]:                   'Completed',
  [StatusOrders.CANCELLED]:                   'Cancelled',
  [StatusOrders.CANCELLATION_REQUESTED]:      'Cancellation Requested',
  [StatusOrders.ACTION_REQUIRED_BY_CUSTOMER]: 'Action Required by Customer',
  [StatusOrders.REQUEST_DOCUMENT]:            'Request Document',
  [StatusOrders.CONTACT_BUYER]:               'Contact Buyer',
  [StatusOrders.DOUBLED_ORDER]:               'Doubled Order',
  [StatusOrders.PAID]:                        'Paid',
  [StatusOrders.RETURN_REQUEST]:              'Return Request',
  [StatusOrders.REFUNDED]:                    'Refunded',
  [StatusOrders.PARTIAL_REFUND]:              'Partial Refund',
  [StatusOrders.SHIPPED]:                     'Shipped',
  [StatusOrders.STRIPE_PENDING]:              'Stripe Pending',
  [StatusOrders.PAYPAL_PENDING]:              'Paypal Pending',
  [StatusOrders.NPA_PENDING]:                 'NPA Pending',
  [StatusOrders.STRIPE_COMPLETED]:            'Stripe Completed',
  [StatusOrders.PAYPAL_COMPLETED]:            'Paypal Completed',
  [StatusOrders.NPA_COMPLETED]:               'NPA Completed',
  [StatusOrders.PAYMENT_ACTION_REQUEST]:      'Payment Action Request',
};

export const PAYMENT_METHOD_STYLE: Record<
  string,
  { wrapper: string; label: string; iconName?: IconName; imageName?: ImageName; lucideIcon?: LucideIcon; iconColor?: string }
> = {
  [PaymentMethod.STRIPE]:        { wrapper: 'bg-primary-s50', label: 'Stripe',        imageName: 'stripe' },
  [PaymentMethod.PAYPAL]:        { wrapper: 'bg-primary-s50', label: 'PayPal',        iconName: 'paypal' },
  [PaymentMethod.NPA]:           { wrapper: 'bg-primary-s50', label: 'Credit Card',   iconName: 'card' },
  [PaymentMethod.CREDIT_CARD]:   { wrapper: 'bg-primary-s50', label: 'Credit Card',   imageName: 'stripe' },
  [PaymentMethod.BANK_TRANSFER]: { wrapper: 'bg-primary-s50', label: 'Bank Transfer', iconName: 'bank-transfer' },
  [PaymentMethod.SEND_REQUEST]:  { wrapper: 'bg-primary-s50', label: 'Send Request',  iconName: 'send-request' },
  [PaymentMethod.ZELLE]:         { wrapper: 'bg-primary-s50', label: 'Zelle',         lucideIcon: Building, iconColor: 'text-primary-s500' },
  [PaymentMethod.CHECK]:         { wrapper: 'bg-primary-s50', label: 'Check',         iconName: 'payment-check' },
  [PaymentMethod.ACH]:           { wrapper: 'bg-primary-s50', label: 'ACH',           iconName: 'ach-transfer' },
  [PaymentMethod.WIRE]:          { wrapper: 'bg-primary-s50', label: 'Wire Transfer', iconName: 'bank-transfer' },
  [PaymentMethod.CASH]:          { wrapper: 'bg-primary-s50', label: 'Cash',          lucideIcon: Banknote, iconColor: 'text-primary-s500' },
  [PaymentMethod.OTHER]:         { wrapper: 'bg-primary-s50', label: 'Other',         lucideIcon: CircleDollarSign, iconColor: 'text-primary-s500' },
};

export const PAYMENT_STATUS_STYLE: Record<string, { wrapper: string; icon: string }> = {
  [PaymentStatus.PAID]:           { wrapper: 'bg-success-light border border-success',  icon: 'text-success' },
  [PaymentStatus.UNPAID]:         { wrapper: 'bg-warning-light border border-warning',  icon: 'text-warning' },
  [PaymentStatus.AUTHORIZED]:     { wrapper: 'bg-info-light border border-info',        icon: 'text-info' },
  [PaymentStatus.PARTIAL_CAPTURE]:{ wrapper: 'bg-info-light border border-info',        icon: 'text-info' },
  [PaymentStatus.PARTIAL_REFUND]: { wrapper: 'bg-warning-light border border-warning',  icon: 'text-warning' },
  [PaymentStatus.REFUNDED]:       { wrapper: 'bg-smoke border border-dark5',            icon: 'text-lightGray' },
  [PaymentStatus.RETURNED]:       { wrapper: 'bg-danger-light border border-danger',    icon: 'text-danger' },
};

export const DEFAULT_PAYMENT_STATUS_STYLE = {
  wrapper: 'bg-smoke border border-dark5',
  icon: 'text-lightGray',
};

export const COUNTRY_FLAG: Record<string, string> = {
  US: '🇺🇸', USA: '🇺🇸',
  GB: '🇬🇧', UK: '🇬🇧',
  CA: '🇨🇦',
  AU: '🇦🇺',
  DE: '🇩🇪',
  FR: '🇫🇷',
};

export const VOID_REASONS: { label: string; value: string }[] = [
  { label: 'Duplicate',        value: 'duplicate' },
  { label: 'Fraudulent',       value: 'fraudulent' },
  { label: 'Customer Request', value: 'requested_by_customer' },
];

export const REFUND_REASONS: { label: string; value: string }[] = [
  { label: 'Duplicate',             value: 'duplicate' },
  { label: 'Fraudulent',            value: 'fraudulent' },
  { label: 'Requested by Customer', value: 'requested_by_customer' },
];

export interface MetadataRequest {
  status: string;
  reason?: string;
  requestedBy?: string;
  requestedAt?: string;
}

export function getMetadataRequest(
  metadata: Record<string, unknown> | null,
  key: 'cancellationRequest' | 'returnRequest'
): MetadataRequest | null {
  const req = metadata?.[key];
  if (!req || typeof req !== 'object') return null;
  return req as MetadataRequest;
}

export function MetadataRequestBadges({
  metadata,
  size = 'md',
}: {
  metadata: Record<string, unknown> | null;
  size?: 'sm' | 'md';
}) {
  const cancel = getMetadataRequest(metadata, 'cancellationRequest');
  const ret = getMetadataRequest(metadata, 'returnRequest');

  const isCancelActive = cancel?.status === 'pending';
  const isReturnActive = ret?.status === 'pending' || ret?.status === 'requested';

  if (!isCancelActive && !isReturnActive) return null;

  const px = size === 'sm' ? 'px-2.5' : 'px-3';
  const text = size === 'sm' ? 'text-[11px]' : 'text-xs';

  return (
    <>
      {isCancelActive && (
        <div className="flex flex-col gap-0.5">
          <span className={`w-fit rounded-full bg-danger-light ${px} py-0.5 ${text} font-semibold text-danger`}>
            Cancellation Pending
          </span>
          {cancel.reason && (
            <p className={`truncate ${text} font-medium text-lightGray`}>{cancel.reason}</p>
          )}
        </div>
      )}
      {isReturnActive && (
        <div className="flex flex-col gap-0.5">
          <span className={`w-fit rounded-full bg-danger-light ${px} py-0.5 ${text} font-semibold text-danger`}>
            Return Request
          </span>
          {ret.reason && (
            <p className={`truncate ${text} font-medium text-lightGray`}>{ret.reason}</p>
          )}
        </div>
      )}
    </>
  );
}

export function formatCurrency(amount: string) {
  return parseFloat(amount || '0').toFixed(2);
}

/** MM-DD-YYYY HH:mm — used in list view */
export function formatDateShort(dateString: string) {
  const d = new Date(dateString);
  const mm  = String(d.getMonth() + 1).padStart(2, '0');
  const dd  = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh  = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}-${dd}-${yyyy} ${hh}:${min}`;
}

/** "January 1, 2026, 09:00 AM" — used in order detail */
export function formatDateLong(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "27 Feb 2026, 14:12" — used in order grid card */
export function formatDateCard(dateString: string) {
  const d = new Date(dateString);
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year}, ${hh}:${mm}`;
}

export function getInitialShippingType(status: string): ShippingType {
  const values = Object.values(ShippingType) as string[];
  return values.includes(status) ? (status as ShippingType) : ShippingType.PENDING;
}

// Ordered by value ascending so the map reads like a progress bar left → right
export const SHIPPING_TYPE_PROGRESS: Record<ShippingType, { value: number; color: string; textColor: string; label: string }> = {
  [ShippingType.PENDING]:                { value: 10,  color: '#A1AFBF', textColor: 'text-lightGray',    label: 'Pending' },
  [ShippingType.CONTACT_BUYER]:          { value: 15,  color: '#FFC233', textColor: 'text-warning',      label: 'Contact Buyer' },
  [ShippingType.AWAITING_PAYMENT]:       { value: 20,  color: '#FFC233', textColor: 'text-warning',      label: 'Awaiting Payment' },
  [ShippingType.DOUBLED_ORDER]:          { value: 25,  color: '#F97316', textColor: 'text-orange-500',   label: 'Doubled Order' },
  [ShippingType.ACTION_REQUIRED]:        { value: 30,  color: '#FEECF1', textColor: 'text-danger',       label: 'Action Required' },
  [ShippingType.REQUEST_DOCUMENT]:       { value: 35,  color: '#F53D6B', textColor: 'text-danger',       label: 'Request Document' },
  [ShippingType.PAYMENT_ACTION_REQUEST]: { value: 38,  color: '#FFC233', textColor: 'text-warning',      label: 'Payment Action Request' },
  [ShippingType.STRIPE_PENDING]:         { value: 40,  color: '#FFC233', textColor: 'text-warning',      label: 'Stripe Pending' },
  [ShippingType.PAYPAL_PENDING]:         { value: 40,  color: '#FFC233', textColor: 'text-warning',      label: 'Paypal Pending' },
  [ShippingType.NPA_PENDING]:            { value: 40,  color: '#FFC233', textColor: 'text-warning',      label: 'NPA Pending' },
  [ShippingType.STRIPE_COMPLETED]:       { value: 45,  color: '#E8F3FF', textColor: 'text-primary-s500', label: 'Stripe Completed' },
  [ShippingType.PAYPAL_COMPLETED]:       { value: 45,  color: '#E8F3FF', textColor: 'text-primary-s500', label: 'Paypal Completed' },
  [ShippingType.NPA_COMPLETED]:          { value: 45,  color: '#E8F3FF', textColor: 'text-primary-s500', label: 'NPA Completed' },
  [ShippingType.PAID]:                   { value: 50,  color: '#1565C0', textColor: 'text-primary-s500', label: 'Paid' },
  [ShippingType.PROCESSING]:             { value: 70,  color: '#7C3AED', textColor: 'text-purple-600',   label: 'Processing' },
  [ShippingType.SHIPPED]:                { value: 90,  color: '#17BD8D', textColor: 'text-success',      label: 'Shipped' },
  [ShippingType.RETURN_REQUEST]:         { value: 90,  color: '#F53D6B', textColor: 'text-danger',       label: 'Return Request' },
  [ShippingType.PARTIAL_REFUND]:         { value: 95,  color: '#FFC233', textColor: 'text-warning',      label: 'Partial Refund' },
  [ShippingType.REFUNDED]:               { value: 100,  color: '#F53D6B', textColor: 'text-danger',       label: 'Refunded' },
  [ShippingType.COMPLETED]:              { value: 100, color: '#17BD8D', textColor: 'text-success',      label: 'Completed' },
  [ShippingType.CANCELLED]:              { value: 100,  color: '#A1AFBF', textColor: 'text-lightGray',    label: 'Cancelled' },
  [ShippingType.CANCELLATION_REQUESTED]: { value: 100,  color: '#F53D6B', textColor: 'text-danger',       label: 'Cancellation Requested' },
};

export const FLAG_STYLE: Record<string, { label: string; color: string }> = {
  green:  { label: 'Green',  color: 'text-success' },
  orange: { label: 'Orange', color: 'text-orange-500' },
  red:    { label: 'Red',    color: 'text-danger' },
};

export const FRAUD_STATUS_STYLE: Record<string, { label: string; color: string }> = {
  approved: { label: 'APPROVED', color: 'text-success' },
  pending:  { label: 'PENDING',  color: 'text-warning' },
  review:   { label: 'REVIEW',   color: 'text-info' },
  rejected: { label: 'REJECTED', color: 'text-danger' },
  flagged:  { label: 'FLAGGED',  color: 'text-orange-500' },
};

export const ProgressBar = memo(function ProgressBar({
  value = 40,
  color = '#1565C0',
}: {
  value?: number;  // 0–100 percentage
  color?: string;  // hex color
}) {
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div
      className="h-2.5 w-full overflow-hidden rounded-full"
      style={{ backgroundColor: `${color}33` }}
    >
      <div
        className="h-full rounded-full"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
});

// ─── Storefront logo ──────────────────────────────────────────────────────────

const STOREFRONT_IMAGE: Record<string, string> = {
  aftermarket: aftermarketImg,
  amazon:      amazonImg,
  ebay:        ebayImg,
  walmart:     walmartImg,
  zoho:        zohoImg,
};

function getStorefrontImage(name: string): string | null {
  const key = name.toLowerCase().replace(/[\s.]/g, '');
  for (const [k, img] of Object.entries(STOREFRONT_IMAGE)) {
    if (key.includes(k)) return img;
  }
  return null;
}

export const StorefrontLogo = memo(function StorefrontLogo({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  const img = getStorefrontImage(name);
  if (img) {
    return (
      <img
        src={img}
        alt={name}
        className={className ?? 'h-10 w-auto max-w-40 object-contain'}
      />
    );
  }
  return (
    <img
      src={gtEngine}
      alt={name}
      className={className ?? 'h-10 w-auto max-w-40 object-contain'}
    />
  );
});

// ─── Shared constants ────────────────────────────────────────────────────────

// Ordered to match SHIPPING_TYPE_PROGRESS (ascending value)
export const SHIPPING_TYPE_OPTIONS: ShippingType[] = [
  ShippingType.PENDING,
  ShippingType.CONTACT_BUYER,
  ShippingType.AWAITING_PAYMENT,
  ShippingType.CANCELLED,
  ShippingType.DOUBLED_ORDER,
  ShippingType.ACTION_REQUIRED,
  ShippingType.CANCELLATION_REQUESTED,
  ShippingType.PAYMENT_ACTION_REQUEST,
  ShippingType.STRIPE_PENDING,
  ShippingType.PAYPAL_PENDING,
  ShippingType.STRIPE_COMPLETED,
  ShippingType.PAYPAL_COMPLETED,
  ShippingType.PAID,
  ShippingType.PROCESSING,
  ShippingType.SHIPPED,
  ShippingType.RETURN_REQUEST,
  ShippingType.REFUNDED,
  ShippingType.COMPLETED,
];

// ─── Shared props for OrderCardGrid / OrderCardList ───────────────────────────

export interface OrderCardUIProps {
  order: Orders;
  /** Digits-only order number shown in list view header */
  orderNum: string;
  /** First 9 chars of id, uppercased — shown in grid view header */
  shortId: string;
  storeName: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  billingLine: string;
  shippingLine: string;
  flagEmoji: string | null;
  salespersonName: string | null;
  paymentMethodStyle: { wrapper: string; label: string; iconName?: IconName; imageName?: ImageName; lucideIcon?: LucideIcon; iconColor?: string } | null;
  paymentMethodLabel: string;
  paymentStatusStyle: { wrapper: string; icon: string };
  flagInfo: { label: string; color: string } | null;
  progress: { value: number; color: string; textColor: string; label: string };
  shippingType: ShippingType;
  isApproved: boolean;
  isStaff: boolean;
  noteOpen: boolean;
  paymentOpen: boolean;
  salespersonOpen: boolean;
  isSavingNote: boolean;
  onCardClick: () => void;
  onShippingTypeSelect: (type: ShippingType) => void;
  onNoteOpenChange: (open: boolean) => void;
  onPaymentOpenChange: (open: boolean) => void;
  onSalespersonOpenChange: (open: boolean) => void;
  onSaveNote: (text: string) => void;
}

// ─── Payment method icon ──────────────────────────────────────────────────────

export const PaymentIcon = memo(function PaymentIcon({
  iconName,
  imageName,
  lucideIcon: LucideIconComponent,
  fallbackChar,
  size,
  wrapperClassName,
  iconColor,
}: {
  iconName?: IconName;
  imageName?: ImageName;
  lucideIcon?: LucideIcon;
  wrapper?: string;
  fallbackChar?: string;
  size: number;
  wrapperClassName?: string;
  iconColor?: string;
}) {
  const containerClass =
    wrapperClassName ??
    `flex items-center justify-center rounded-xl bg-primary-s50`;
  const innerSize = Math.round(size * 0.55);

  if (imageName) {
    return (
      <div className={containerClass} style={{ width: size, height: size }}>
        <DynamicImage name={imageName} size={innerSize} alt="" />
      </div>
    );
  }
  if (iconName) {
    return (
      <div className={containerClass} style={{ width: size, height: size }}>
        <DynamicIcon name={iconName} size={innerSize} color="white" />
      </div>
    );
  }
  if (LucideIconComponent) {
    return (
      <div className={containerClass} style={{ width: size, height: size }}>
        <LucideIconComponent size={innerSize} className={iconColor ?? 'text-white'} />
      </div>
    );
  }
  return (
    <div className={containerClass} style={{ width: size, height: size }}>
      <span className="text-sm font-bold text-primary-s500">
        {fallbackChar ?? '$'}
      </span>
    </div>
  );
});
